import { client } from '../../../apiClient';
import { format } from 'date-fns';
import { getDefaultRange } from '../../../../utils/utils';
import { ChatType } from '../../../../types/monitor/ChatType';
import { SourceDataResponse } from '../../../../models/monitor/data/sources/SourceDataResponse';
import { MonitorType } from '../../../../types/monitor/MonitorType';
import { DomainsStatsResponse } from '../../../../models/monitor/data/sources/DomainsStatsResponse';
import { TypesStatsResponse } from '../../../../models/monitor/data/sources/TypesStatsResponse';
import { DomainsDailyStatsResponse } from '../../../../models/monitor/data/sources/DomainsDailyStatsResponse';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';
import { SourcesStatsResponse } from '../../../../models/monitor/data/sources/SourcesStatsResponse';
import { CategoriesStatsResponse } from '../../../../models/monitor/data/sources/CategoriesStatsResponse';

const SOURCES_ENDPOINT = '/monitor/data/sources';

export const getSourcesForProject = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<SourceDataResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const params: Record<string, string> = {};
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.promptIds = monitorPromptIds.join(',');
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    params.models = filterMonitorTypes.join(',');
  }
  const requestConfig = Object.keys(params).length > 0 ? { params } : undefined;

  const { data } = await client(true, true).get<SourceDataResponse>(
    `${SOURCES_ENDPOINT}/project/${projectId}/${type}/${formattedFrom}/${formattedTo}`,
    requestConfig
  );

  return data;
};

export const getSourcesForMonitor = async (
  id: string,
  keyword: string,
  type?: MonitorType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  models?: string[]
): Promise<SourceDataResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam = models && models.length > 0 ? `?models=${models.join(',')}` : '';

  return client(true, true)
    .get<SourceDataResponse>(
      `${SOURCES_ENDPOINT}/monitor/${id}/${keyword}/${type}/${formattedFrom}/${formattedTo}${modelsParam}`
    )
    .then((response) => response.data)
    .catch((error) => error);
};

export const getDomainsStats = async (
  projectId: number,
  selectedFirst: boolean,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  page?: number,
  pageSize?: number,
  domain?: string[] | null | undefined,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<DomainsStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  // Always add pagination params (backend will use defaults if not provided, but we'll always send them)
  const pageParam = page !== undefined && page !== null ? page : 1;
  const pageSizeParam = pageSize !== undefined && pageSize !== null ? pageSize : 5;

  const queryParams = new URLSearchParams();
  queryParams.append('page', pageParam.toString());
  queryParams.append('pageSize', pageSizeParam.toString());

  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  queryParams.append('selectedFirst', selectedFirst.toString());

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/domains-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<DomainsStatsResponse>(url);
  return data;
};

export const getTypesStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  domain?: string[] | null | undefined,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<TypesStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const queryParams = new URLSearchParams();
  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const { data } = await client(true, true).get<TypesStatsResponse>(
    `${SOURCES_ENDPOINT}/project/types-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}/${
      queryString ? `?${queryString}` : ''
    }`
  );
  return data;
};

export const getDomainsDailyStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  period?: MonitorPeriod | 'DAILY',
  from?: Date | null,
  to?: Date | null,
  domain?: string[] | null | undefined,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<DomainsDailyStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const queryParams = new URLSearchParams();

  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/domains-daily-stats/${projectId}/${type}/${period}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<DomainsDailyStatsResponse>(url);
  return data;
};

export interface SourcesDailyStatsResponse {
  [date: string]: { source: string; count: number }[];
}

export const getSourcesDailyStatsForDomain = async (
  projectId: number,
  domain: string,
  type?: ChatType | 'ALL',
  period?: MonitorPeriod | 'DAILY',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<SourcesDailyStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/sources-daily-stats/${projectId}/${encodedDomain}/${type}/${period}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<SourcesDailyStatsResponse>(url);
  return data;
};

export interface UrlTypesStatsResponse {
  pageType: string;
  count: number;
}

export const getUrlTypesStatsForDomain = async (
  projectId: number,
  domain: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<UrlTypesStatsResponse[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/url-types-stats/${projectId}/${encodedDomain}/${type}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<UrlTypesStatsResponse[]>(url);
  return data;
};

export const getSourcesStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  page?: number,
  pageSize?: number,
  domain?: string[] | null | undefined,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<SourcesStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  // Always add pagination params (backend will use defaults if not provided, but we'll always send them)
  const pageParam = page !== undefined && page !== null ? page : 1;
  const pageSizeParam = pageSize !== undefined && pageSize !== null ? pageSize : 10;

  const queryParams = new URLSearchParams();
  queryParams.append('page', pageParam.toString());
  queryParams.append('pageSize', pageSizeParam.toString());

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/sources-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<SourcesStatsResponse>(url);
  return data;
};

export const getKeySourcesStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  page?: number,
  pageSize?: number,
  domain?: string[] | null | undefined,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<SourcesStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  // Always add pagination params (backend will use defaults if not provided, but we'll always send them)
  const pageParam = page !== undefined && page !== null ? page : 1;
  const pageSizeParam = pageSize !== undefined && pageSize !== null ? pageSize : 10;

  const queryParams = new URLSearchParams();
  queryParams.append('page', pageParam.toString());
  queryParams.append('pageSize', pageSizeParam.toString());

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/key-sources-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<SourcesStatsResponse>(url);
  return data;
};

export const getDomainsWithSourcesStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  page?: number,
  pageSize?: number,
  domain?: string[] | null | undefined,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<DomainsStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  // Always add pagination params (backend will use defaults if not provided, but we'll always send them)
  const pageParam = page !== undefined && page !== null ? page : 1;
  const pageSizeParam = pageSize !== undefined && pageSize !== null ? pageSize : 10;

  const queryParams = new URLSearchParams();
  queryParams.append('page', pageParam.toString());
  queryParams.append('pageSize', pageSizeParam.toString());

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  if (domain) {
    domain.forEach((d) => queryParams.append('domain', d));
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/domains-with-sources-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<DomainsStatsResponse>(url);
  return data;
};

export const getCategoriesStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  useSourceCategory?: boolean,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<CategoriesStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const params: Record<string, string> = {};
  if (useSourceCategory) {
    params.useSourceCategory = 'true';
  }
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.promptIds = monitorPromptIds.join(',');
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    params.models = filterMonitorTypes.join(',');
  }
  const { data } = await client(true, true).get<CategoriesStatsResponse>(
    `${SOURCES_ENDPOINT}/project/categories-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}`,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  return data;
};

export interface DomainSourceStats {
  source: string;
  domain: string;
  title?: string;
  pageType: string;
  isVerified: boolean;
  brandMentioned: boolean | null;
  brands: string[];
  count: number;
  promptCount: number;
  share: number;
  citationRate: number;
  firstSeen: string;
  lastSeen: string;
  prompts: { keyword: string; monitorPromptId: number; monitorDataId: number; createdAt: string; snippet: string; queriesCount: number }[];
}

export interface DomainSourcesStatsResponse {
  sources: DomainSourceStats[];
  pagination?: { page: number; pageSize: number; totalItems: number; totalPages: number };
  totalCount: number;
}

export const getSourcesStatsForDomain = async (
  projectId: number,
  domain: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  page?: number,
  pageSize?: number,
  keyword?: string,
  mentioned?: boolean,
  sortBy?: string,
  sortOrder?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<DomainSourcesStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const pageParam = page !== undefined && page !== null ? page : 1;
  const pageSizeParam = pageSize !== undefined && pageSize !== null ? pageSize : 10;

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  queryParams.append('page', pageParam.toString());
  queryParams.append('pageSize', pageSizeParam.toString());

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  if (mentioned !== undefined) {
    queryParams.append('mentioned', String(mentioned));
  }

  if (sortBy) {
    queryParams.append('sortBy', sortBy);
  }
  if (sortOrder) {
    queryParams.append('sortOrder', sortOrder);
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/sources-stats-for-domain/${projectId}/${encodedDomain}/${type}/${formattedFrom}/${formattedTo}/${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<DomainSourcesStatsResponse>(url);
  return data;
};

export interface BrandStat {
  brand: string;
  mentionCount: number;
  sourceCount: number;
  /**
   * Coverage: percentage of this domain's total citations that contain the brand.
   * Formula: (mentionCount / total_citations_for_domain) * 100
   * Multiple brands can appear in the same citation, so coverage values do not need
   * to sum to 100.
   */
  coverage: number;
}

export interface BrandStatsForDomainResponse {
  brands: BrandStat[];
}

export const getBrandsStatsForDomain = async (
  projectId: number,
  domain: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<BrandStatsForDomainResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/brands-stats-for-domain/${projectId}/${encodedDomain}/${type}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<BrandStatsForDomainResponse>(url);
  return data;
};

export interface PlatformStat {
  platform: string;
  count: number;
  share: number;
}

export interface PlatformStatsForDomainResponse {
  platforms: PlatformStat[];
}

export const getPlatformStatsForDomain = async (
  projectId: number,
  domain: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<PlatformStatsForDomainResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/platform-stats-for-domain/${projectId}/${encodedDomain}/${type}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<PlatformStatsForDomainResponse>(url);
  return data;
};

export const getPlatformStatsForSource = async (
  projectId: number,
  source: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<PlatformStatsForDomainResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedSource = encodeURIComponent(source);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/platform-stats-for-source/${projectId}/${encodedSource}/${type}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<PlatformStatsForDomainResponse>(url);
  return data;
};

export interface ChatForDomainRow {
  chatId: number;
  monitorPromptId: number;
  promptName: string;
  responseSnippet: string;
  citationCount: number;
  mentions: string[];
  sourceDomains: string[];
  created: string;
}

export interface ChatsForDomainResponse {
  chats: ChatForDomainRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export const getChatsForDomain = async (
  projectId: number,
  domain: string,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  page: number = 1,
  pageSize: number = 10,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<ChatsForDomainResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('pageSize', pageSize.toString());

  if (keyword) {
    queryParams.append('keyword', keyword);
  }

  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }

  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/chats-for-domain/${projectId}/${encodedDomain}/${chatType}/${formattedFrom}/${formattedTo}${
    queryString ? `?${queryString}` : ''
  }`;

  const { data } = await client(true, true).get<ChatsForDomainResponse>(url);
  return data;
};

export interface BrandForSourceRow {
  brand: string;
  position: number | null;
  mentionCount: number | null;
}

export interface BrandsForSourceResponse {
  brands: BrandForSourceRow[];
}

export const getBrandsForSource = async (
  projectId: number,
  source: string,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<BrandsForSourceResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedSource = encodeURIComponent(source);

  const queryParams = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }
  const queryString = queryParams.toString();
  const url = `${SOURCES_ENDPOINT}/project/brands-for-source/${projectId}/${encodedSource}/${type}/${formattedFrom}/${formattedTo}${queryString ? `?${queryString}` : ''}`;

  const { data } = await client(true, true).get<BrandsForSourceResponse>(url);
  return data;
};

export const exportDomainsStats = async (
  projectId: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const { data } = await client(true, true).get<Blob>(
    `${SOURCES_ENDPOINT}/project/export-domains-stats/${projectId}/${type}/${formattedFrom}/${formattedTo}`,
    {
      responseType: 'blob',
    }
  );
  return data;
};

export type PageMovementMode = 'top' | 'topGainers' | 'topDecliners' | 'newlyCited' | 'droppedOff';

export interface PageMovementPromptRow {
  monitorPromptId: number;
  monitorDataId: number;
  /** Prompt text (`monitor_prompt.prompt`). Field is named `keyword` for parity with
   *  `DomainSourceStats.prompts[].keyword`. */
  keyword: string;
  /** First sentence of the LLM response, capped at 150 chars (empty when unavailable). */
  snippet: string;
  createdAt: string;
}

export interface PageMovementRow {
  source: string;
  title: string | null;
  models: string[];
  firstSeenEver: string;
  lastUsedInPeriod: string | null;
  currentCount: number;
  previousCount: number;
  /** Absolute change in retrieval count: currentCount - previousCount. Plain integer,
   *  NOT a percentage. Null for newlyCited / droppedOff. */
  delta: number | null;
  trend: number[];
  prompts: PageMovementPromptRow[];
}

export interface PageMovementResponse {
  rows: PageMovementRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export const getPageMovementForDomain = async (
  projectId: number,
  domain: string,
  mode: PageMovementMode,
  page: number,
  pageSize: number,
  type?: ChatType | 'ALL',
  from?: Date | null,
  to?: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<PageMovementResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const encodedDomain = encodeURIComponent(domain);

  const queryParams = new URLSearchParams();
  queryParams.append('mode', mode);
  queryParams.append('page', String(page));
  queryParams.append('pageSize', String(pageSize));
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    queryParams.append('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    queryParams.append('models', filterMonitorTypes.join(','));
  }

  const url = `${SOURCES_ENDPOINT}/project/page-movement-for-domain/${projectId}/${encodedDomain}/${type}/${formattedFrom}/${formattedTo}?${queryParams.toString()}`;

  const { data } = await client(true, true).get<PageMovementResponse>(url);
  return data;
};
